import React from 'react';
import { useSelector } from 'react-redux';

// Types
import { GoalItem } from 'Shared/constants/types';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Components
import { WpDataTableV2, SortDirections } from 'Wpds/components/table-v2';
import GoalName from '../GoalName/GoalName';
import GoalStatus from '../GoalStatus/GoalStatus';
import GoalOwner from '../GoalOwner/GoalOwner';
import GoalWorkspace from '../GoalWorkspace/GoalWorkspace';
import GoalDepartment from '../GoalDepartment/GoalDepartment';
import GoalDueDate from '../GoalDueDate/GoalDueDate';
import GoalActions from './partials/GoalActions';
import GoalProgress from '../GoalProgress/GoalProgress';

export enum GoalTableColumns {
  name = 'name',
  due = 'due',
  owner = 'owner',
  workspace = 'workspace',
  department = 'department',
  status = 'status',
  progress = 'progress',
  actions = 'actions',
}

export const ColumnsConst = {
  [GoalTableColumns.name]: {
    label: 'Goal Name',
    canHide: false,
  },
  [GoalTableColumns.due]: {
    label: 'Due',
    canHide: true,
  },
  [GoalTableColumns.owner]: {
    label: 'Owner',
    canHide: true,
  },
  [GoalTableColumns.workspace]: {
    label: 'Workspace',
    canHide: true,
  },
  [GoalTableColumns.department]: {
    label: 'Department',
    canHide: true,
  },
  [GoalTableColumns.status]: {
    label: 'Status',
    canHide: true,
  },
  [GoalTableColumns.progress]: {
    label: 'Progress',
    canHide: true,
  },
  [GoalTableColumns.actions]: {
    label: 'Actions',
    canHide: false,
  },
};

type Props = {
  goals: GoalItem[];
  onRowClick?: Function;
  hiddenColumns?: Array<GoalTableColumns>;
  handleMarkComplete?: Function;
  handleMarkIncomplete?: Function;
  handleChangeStatus?: Function;
  redirectToGoalDetailPage?: Function;
  showTableHeader?: boolean;
  slimRow?: boolean;
  showBorder?: boolean;
  components?: { [key in GoalTableColumns]?: Function };
  showInContainer?: boolean;
  windowedScrolling?: boolean;
  maxHeight?: string;
};

const GoalsTable = ({
  goals,
  onRowClick,
  hiddenColumns = [],
  handleMarkComplete,
  handleMarkIncomplete,
  handleChangeStatus,
  redirectToGoalDetailPage,
  showTableHeader = true,
  showInContainer = true,
  slimRow = false,
  showBorder = true,
  components,
  windowedScrolling = false,
  maxHeight,
}: Props) => {
  const users = useSelector(state => UsersSelectors.getAllUsers(state));

  const columns = [
    {
      label: ColumnsConst.name.label,
      selector: row => row.name,
      // component: ({ row }) => <GoalName goal={row} />,
      component: ({ row }) => {
        const CustomComponent = components?.[GoalTableColumns.name];
        if (CustomComponent) return <CustomComponent goal={row} />;
        return <GoalName goal={row} />;
      },
      showColumn: !hiddenColumns.includes(GoalTableColumns.name),
      maxWidth: '25rem',
    },
    {
      label: ColumnsConst.due.label,
      selector: row => row.endDate,
      component: ({ row }) => {
        const CustomComponent = components?.[GoalTableColumns.due];
        if (CustomComponent) return <CustomComponent goal={row} />;
        return <GoalDueDate goal={row} />;
      },
      sort: SortDirections.ASC,
      showColumn: !hiddenColumns.includes(GoalTableColumns.due),
    },
    {
      label: ColumnsConst.owner.label,
      selector: row => users.getIn([row.assigneeId, 'label']),
      component: ({ row }) => {
        const CustomComponent = components?.[GoalTableColumns.owner];
        if (CustomComponent) return <CustomComponent goal={row} />;
        return <GoalOwner goal={row} />;
      },
      showColumn: !hiddenColumns.includes(GoalTableColumns.owner),
    },
    {
      label: ColumnsConst[GoalTableColumns.workspace].label,
      component: ({ row }) => {
        const CustomComponent = components?.[GoalTableColumns.workspace];
        if (CustomComponent) return <CustomComponent goal={row} />;
        return <GoalWorkspace goal={row} />;
      },
      showColumn: !hiddenColumns.includes(GoalTableColumns.workspace),
    },
    {
      label: ColumnsConst.department.label,
      selector: row => users.getIn([row.assigneeId, 'department']),
      component: ({ row }) => {
        const CustomComponent = components?.[GoalTableColumns.department];
        if (CustomComponent) return <CustomComponent goal={row} />;
        return <GoalDepartment goal={row} />;
      },
      showColumn: !hiddenColumns.includes(GoalTableColumns.department),
    },
    {
      label: ColumnsConst.status.label,
      selector: row => row.status,
      component: ({ row }) => {
        const CustomComponent = components?.[GoalTableColumns.status];
        if (CustomComponent) return <CustomComponent goal={row} />;
        return <GoalStatus goal={row} />;
      },
      showColumn: !hiddenColumns.includes(GoalTableColumns.status),
    },
    {
      label: ColumnsConst.progress.label,
      component: ({ row }) => {
        const CustomComponent = components?.[GoalTableColumns.progress];
        if (CustomComponent) return <CustomComponent goal={row} />;
        return <GoalProgress goal={row} />;
      },
      showColumn: !hiddenColumns.includes(GoalTableColumns.progress),
    },
    {
      label: ColumnsConst.actions.label,
      hideLabel: true,
      isSortable: false,
      rightAlign: true,
      showColumn: !hiddenColumns.includes(GoalTableColumns.actions),
      component: ({ row }) => {
        const CustomActions = components?.[GoalTableColumns.actions];
        return (
          <>
            {CustomActions ? (
              <CustomActions goal={row} />
            ) : (
              <GoalActions
                goal={row}
                handleMarkComplete={() => handleMarkComplete(row.slug)}
                handleMarkIncomplete={handleMarkIncomplete}
                handleChangeStatus={handleChangeStatus}
                redirectToGoalDetailPage={redirectToGoalDetailPage}
              />
            )}
          </>
        );
      },
    },
  ];

  return (
    <WpDataTableV2
      tableId="goalsTable"
      data={goals}
      columns={columns}
      rowClick={onRowClick}
      showHeader={showTableHeader}
      showInContainer={showInContainer}
      slimRow={slimRow}
      showBorder={showBorder}
      windowedScrolling={windowedScrolling}
      maxHeight={maxHeight}
    />
  );
};

export default GoalsTable;
